//@ts-nocheck
/**
 * <exam-selector-component>
 * Shared exam/cohort picker: restore display, selectExam, modal drag-handle.
 * API instances fetch exam_landing_pages via Storefront GraphQL.
 * SSR instances use Liquid-rendered cards already in the modal.
 */

const CACHE_KEY = "examSelectorCategoryGroups";
const PREFERENCES_KEY = "preferences";
const INIT_FLAG = "data-exam-selector-initialized";
const GRAPHQL_URL = "/api/2025-01/graphql.json";
const EXAM_SELECTOR_QUERY = `
query getExamSelectorData {
  metaobjects(first: 50, type: "exam_category_group", sortKey: "updated_at") {
    nodes {
      id
      handle
      fields {
        key
        value
        type
        reference {
          __typename
          ... on MediaImage {
            image {
              url(transform: { maxWidth: 200 })
            }
          }
        }
        references(first: 50) {
          nodes {
            __typename
            ... on MediaImage {
              image {
                url(transform: { maxWidth: 200 })
              }
            }
          }
        }
      }
    }
  }
}`;

function getCachedExamData() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.categoryGroups) && parsed.categoryGroups.length > 0) {
      return parsed;
    }
  } catch (e) {}
  return null;
}

function setCachedExamData(normalized) {
  try {
    if (normalized && Array.isArray(normalized.categoryGroups)) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(normalized));
    }
  } catch (e) {}
}

function getPreferences() {
  try {
    const raw = localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {}
  return null;
}

function setPreferences(prefs) {
  try {
    if (prefs && typeof prefs === "object") {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
    }
  } catch (e) {}
}

function getField(node, key) {
  if (!node || !node.fields || !Array.isArray(node.fields)) return null;
  for (let i = 0; i < node.fields.length; i++) {
    if (node.fields[i].key === key) return node.fields[i];
  }
  return null;
}

function fieldValue(field) {
  return field && field.value != null ? String(field.value).trim() : "";
}

function parseJsonArray(val) {
  if (val == null || val === "") return [];
  if (Array.isArray(val)) return val;
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [val];
  } catch (e) {
    return val ? [val] : [];
  }
}

function slugify(name) {
  if (name == null) return "";
  return String(name)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function isPlaceholderExam(value) {
  const v = String(value || "").trim();
  return !v || v === "Select Exam" || v === "Select Exam/Class" || v === "Choose your exam";
}

function normalizeExamData(data) {
  const nodes = (data && data.metaobjects && data.metaobjects.nodes) || [];
  const categoryGroups = [];

  for (let n = 0; n < nodes.length; n++) {
    const node = nodes[n];
    const categoryName = fieldValue(getField(node, "category_name"));
    const displayOrderField = getField(node, "display_order");
    let displayOrder = 0;
    if (displayOrderField && displayOrderField.value != null) {
      const num = parseInt(displayOrderField.value, 10);
      if (!isNaN(num)) displayOrder = num;
    }

    const examLandingPagesField = getField(node, "exam_landing_pages");
    let examLandingPages = [];
    if (examLandingPagesField && examLandingPagesField.value) {
      try {
        const parsedValue = JSON.parse(examLandingPagesField.value);
        if (Array.isArray(parsedValue)) examLandingPages = parsedValue;
      } catch (e) {
        examLandingPages = [];
      }
    }

    const examIconsField = getField(node, "exam_icons");
    const iconUrls = [];
    if (examIconsField && examIconsField.references && examIconsField.references.nodes) {
      for (let i = 0; i < examIconsField.references.nodes.length; i++) {
        const media = examIconsField.references.nodes[i];
        const url = media && media.image && media.image.url ? media.image.url : null;
        if (url) iconUrls.push(url);
      }
    }

    const bgColorsField = getField(node, "exam_background_color");
    const bgColors = bgColorsField ? parseJsonArray(bgColorsField.value) : [];

    const defaultIconField = getField(node, "default_exam_icons");
    let defaultIconUrl = null;
    if (defaultIconField && defaultIconField.reference) {
      const ref = defaultIconField.reference;
      if (ref && ref.image && ref.image.url) defaultIconUrl = ref.image.url;
    }

    const exams = [];
    for (let e = 0; e < examLandingPages.length; e++) {
      const landingPage = examLandingPages[e];
      if (!landingPage || typeof landingPage !== "object") continue;
      const examName = landingPage.text != null ? String(landingPage.text).trim() : "";
      let redirectUrl = landingPage.url != null ? String(landingPage.url).trim() : "/";
      if (!examName) continue;
      if (!redirectUrl) redirectUrl = "/";
      exams.push({
        examName: examName,
        examSlug: slugify(examName),
        redirectUrl: redirectUrl,
        iconUrl: (iconUrls.length > 0 && iconUrls[e] != null ? iconUrls[e] : defaultIconUrl) || "",
        bgColor: bgColors[e] != null ? String(bgColors[e]).trim() : "",
      });
    }

    if (categoryName || exams.length > 0) {
      categoryGroups.push({ categoryName, displayOrder, exams });
    }
  }

  categoryGroups.sort(function (a, b) {
    return a.displayOrder - b.displayOrder;
  });

  return { categoryGroups };
}

function normalizePath(url) {
  if (!url) return "";
  let path = String(url).trim();
  try {
    const a = document.createElement("a");
    a.href = path;
    path = a.pathname;
  } catch (e) {}
  path = path.split("?")[0].split("#")[0];
  if (path.length > 1 && path.charAt(path.length - 1) === "/") path = path.slice(0, -1);
  return path;
}

function pathTail(url) {
  const path = normalizePath(url);
  if (!path) return "";
  const parts = path.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
}

function findExamForPath(categoryGroups, pathname) {
  const target = normalizePath(pathname);
  if (!target || target === "/") return null;
  const targetTail = pathTail(target);
  let slugMatch = null;

  for (let g = 0; g < categoryGroups.length; g++) {
    const exams = categoryGroups[g].exams || [];
    for (let e = 0; e < exams.length; e++) {
      const exam = exams[e];
      const examPath = exam.redirectUrl ? normalizePath(exam.redirectUrl) : "";
      if (examPath && examPath === target) return exam;
      if (!slugMatch && targetTail && (exam.examSlug === targetTail || pathTail(examPath) === targetTail)) {
        slugMatch = exam;
      }
    }
  }
  return slugMatch;
}

function writeExamPreferences(examName, examIcon, redirectUrl) {
  const existingPrefs = getPreferences() || {};
  setPreferences({
    exams: [String(examName).replace(/\s+/g, "-")],
    board: existingPrefs.board != null ? String(existingPrefs.board) : "",
    language: existingPrefs.language != null ? String(existingPrefs.language) : "",
    class: existingPrefs.class != null ? String(existingPrefs.class) : "",
    examIcon: String(examIcon || ""),
    redirection_url: redirectUrl,
  });
}

function syncSelectedCohortFromCurrentUrl(categoryGroups) {
  try {
    if (window.__PW_EXAM_COHORT_SYNCED__) return;
    if (!categoryGroups || !categoryGroups.length) return;
    const pathname = (window.location && window.location.pathname) || "";
    if (pathname === "/") return;

    const match = findExamForPath(categoryGroups, pathname);
    if (!match || !match.examName) return;

    let currentName = "";
    try {
      currentName = (localStorage.getItem("selectedExamName") || "").trim();
    } catch (e) {}

    const alreadySelected = currentName && slugify(currentName) === match.examSlug;
    window.__PW_EXAM_COHORT_SYNCED__ = true;
    paintExamSelectorTriggers(match.examName, match.iconUrl || "");
    if (alreadySelected) return;

    try {
      persistExamSelection(match.examName, match.iconUrl || "", match.redirectUrl);
    } catch (e) {}

    try {
      window.dispatchEvent(
        new CustomEvent("examSelected", {
          detail: {
            examName: match.examName,
            examIcon: match.iconUrl,
            redirection_url: match.redirectUrl,
          },
        })
      );
    } catch (e) {}
  } catch (e) {}
}

function syncSelectedCohortFromModalCards(root) {
  try {
    const cards = root && root.querySelectorAll && root.querySelectorAll(".exam-selector-modal__card");
    if (!cards || !cards.length) return;
    const exams = [];
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      exams.push({
        examName: card.getAttribute("data-exam-name") || "",
        examSlug: card.getAttribute("data-exam-slug") || "",
        redirectUrl: card.getAttribute("data-redirect-url") || "",
        iconUrl: card.getAttribute("data-exam-icon") || "",
      });
    }
    syncSelectedCohortFromCurrentUrl([{ exams: exams }]);
  } catch (e) {}
}

function escapeAttr(s) {
  if (s == null) return "";
  return String(s).replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function buildExamSelectorModalInnerHTML(options) {
  const categoryGroups = (options && options.categoryGroups) || [];
  const defaultIconUrl = (options && options.defaultIconUrl) || "";
  let sectionsHTML = "";

  for (let g = 0; g < categoryGroups.length; g++) {
    const group = categoryGroups[g];
    const exams = group.exams || [];
    if (exams.length === 0) continue;

    const listClass = group.displayOrder >= 2 ? " exam-selector-modal__list" : "";
    const cardClass = group.displayOrder >= 2 ? " category-card" : "";
    let cardsHTML = "";
    for (let c = 0; c < exams.length; c++) {
      const exam = exams[c];
      if (!exam.examName) continue;
      const iconUrl = exam.iconUrl || defaultIconUrl;
      const styleAttr = exam.bgColor ? ` style="background: ${escapeAttr(exam.bgColor)}"` : "";
      cardsHTML += `
          <button type="button" class="exam-selector-modal__card exam-card${cardClass}"
            data-exam-name="${escapeAttr(exam.examName)}"
            data-exam-icon="${escapeAttr(iconUrl)}"
            data-exam-slug="${escapeAttr(exam.examSlug)}"
            data-redirect-url="${escapeAttr(exam.redirectUrl)}"${styleAttr}>
            <img src="${escapeAttr(iconUrl)}" alt="${escapeAttr(exam.examName)}"
              class="exam-card__icon" loading="lazy" width="48" height="48">
            <span class="exam-card__name">${escapeAttr(exam.examName)}</span>
          </button>`;
    }
    sectionsHTML += `
        <div class="exam-selector-modal__section exam-category-group">
          <p class="exam-selector-modal__section-title">${escapeAttr(group.categoryName)}</p>
          <div class="exam-selector-modal__grid${listClass}">${cardsHTML}</div>
        </div>`;
  }
  return sectionsHTML;
}

function fetchExamSelectorData(token, query) {
  if (!token || !query) return Promise.resolve({ categoryGroups: [] });
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": token,
    },
    body: JSON.stringify({ query: query }),
  })
    .then(function (res) {
      if (!res.ok)
        return res.text().then(function (t) {
          throw new Error(res.status + " " + t.slice(0, 200));
        });
      return res.json();
    })
    .then(function (result) {
      if (result.errors) return { categoryGroups: [] };
      return normalizeExamData(result.data || {});
    })
    .catch(function () {
      return { categoryGroups: [] };
    });
}

function getPreviousExamName(textEl) {
  const previousExamText = textEl ? String(textEl.textContent || "").trim() : "";
  let previousExamFromStorage = "";
  try {
    previousExamFromStorage = localStorage.getItem("selectedExamName") || "";
  } catch (e) {}

  if (!isPlaceholderExam(previousExamText)) return previousExamText;
  if (!isPlaceholderExam(previousExamFromStorage)) return previousExamFromStorage.trim();
  try {
    const customerCohort = window.__CUSTOMER_DATA__ && window.__CUSTOMER_DATA__.cohort_name;
    if (!isPlaceholderExam(customerCohort)) return String(customerCohort).trim();
  } catch (e) {}
  return null;
}

function persistExamSelection(examName, examIcon, redirectUrl) {
  localStorage.setItem("selectedExamName", examName);
  localStorage.setItem("selectedExamIcon", examIcon);
  localStorage.setItem("selectedExamRedirectUrl", redirectUrl);
  const isLoggedIn = typeof window.hasCustomer !== "undefined" && window.hasCustomer === true;
  if (isLoggedIn) {
    try {
      sessionStorage.setItem("cohortChangedInSession", "true");
    } catch (e) {}
  }
  const existingPrefs = getPreferences();
  const hasAccountExam = existingPrefs && existingPrefs.exams && existingPrefs.exams.length;
  if (!isLoggedIn || !hasAccountExam) {
    writeExamPreferences(examName, examIcon, redirectUrl);
  }
}

function readSavedExamFromStorage() {
  try {
    const selectedName = (localStorage.getItem("selectedExamName") || "").trim();
    const selectedIcon = (localStorage.getItem("selectedExamIcon") || "").trim();
    if (selectedName && !isPlaceholderExam(selectedName)) {
      return { savedName: selectedName, savedIcon: selectedIcon };
    }
    const prefs = getPreferences();
    if (prefs && prefs.exams && prefs.exams.length) {
      const last = prefs.exams[prefs.exams.length - 1];
      const name = typeof last === "string" ? last.trim().replace(/-/g, " ") : "";
      if (name && !isPlaceholderExam(name)) {
        return { savedName: name, savedIcon: (prefs.examIcon && String(prefs.examIcon)) || selectedIcon };
      }
    }
    if (selectedIcon) return { savedName: selectedName, savedIcon: selectedIcon };
  } catch (e) {}
  return { savedName: "", savedIcon: "" };
}

function getExamNavigationUrl(redirectUrl) {
  const url = (redirectUrl || "").trim();
  if (!url || url === "/") return "";
  const target = normalizePath(url);
  if (!target || target === "/") return "";
  const current = normalizePath((window.location && window.location.pathname) || "/");
  if (target === current) return "";
  return target;
}

const EXAM_TEXT_IDS = ["exam-selector-text", "exam-selector-text-account-page", "exam-selector-text-prep"];
const EXAM_ICON_IDS = ["exam-selector-icon", "exam-selector-icon-account-page", "exam-selector-icon-prep"];

function paintExamSelectorTriggers(name, icon) {
  if (!name && !icon) return;
  for (let i = 0; i < EXAM_TEXT_IDS.length; i++) {
    const el = document.getElementById(EXAM_TEXT_IDS[i]);
    if (el && name && !isPlaceholderExam(name)) el.textContent = name;
  }
  for (let i = 0; i < EXAM_ICON_IDS.length; i++) {
    const el = document.getElementById(EXAM_ICON_IDS[i]);
    if (el && icon) {
      el.src = icon;
      el.alt = (name || "Exam") + " Icon";
    }
  }
  const texts = document.querySelectorAll(".exam-selector__text");
  for (let i = 0; i < texts.length; i++) {
    if (name && !isPlaceholderExam(name)) texts[i].textContent = name;
  }
  const icons = document.querySelectorAll("img.exam-selector__icon");
  for (let i = 0; i < icons.length; i++) {
    if (icon) {
      icons[i].src = icon;
      icons[i].alt = (name || "Exam") + " Icon";
    }
  }
  if (name && !isPlaceholderExam(name)) {
    const triggers = document.querySelectorAll(".exam-selector__trigger");
    for (let i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute("aria-label", name);
    }
  }
}

function restoreExamSelectorTriggers() {
  const fromStorage = readSavedExamFromStorage();
  let savedName = fromStorage.savedName;
  let savedIcon = fromStorage.savedIcon;

  if (isPlaceholderExam(savedName)) {
    const fn = window.getSavedExamForDisplay;
    if (typeof fn === "function") {
      try {
        const result = fn() || {};
        if (!isPlaceholderExam(result.savedName)) {
          savedName = result.savedName || "";
          savedIcon = result.savedIcon || savedIcon || "";
        } else if (result.savedIcon && !savedIcon) {
          savedIcon = result.savedIcon;
        }
      } catch (e) {}
    }
  }

  if (isPlaceholderExam(savedName)) {
    const cached = getCachedExamData();
    const groups = cached && cached.categoryGroups;
    if (groups && groups.length) {
      const match = findExamForPath(groups, (window.location && window.location.pathname) || "");
      if (match && match.examName) {
        savedName = match.examName;
        savedIcon = match.iconUrl || savedIcon || "";
      }
    }
  }

  if (!isPlaceholderExam(savedName) || savedIcon) {
    paintExamSelectorTriggers(savedName, savedIcon);
  }
}

window.addEventListener("examDisplayReady", function (ev) {
  const fn = ev.detail && ev.detail.getSavedExamForDisplay;
  if (typeof fn === "function") window.getSavedExamForDisplay = fn;
  restoreExamSelectorTriggers();
});

window.addEventListener("examSelected", function (ev) {
  const detail = (ev && ev.detail) || {};
  if (detail.examName || detail.examIcon) {
    paintExamSelectorTriggers(detail.examName, detail.examIcon);
    return;
  }
  restoreExamSelectorTriggers();
});

function selectExamFromElement(element, options) {
  if (!element) return;
  const examName = element.getAttribute("data-exam-name");
  const examIcon = element.getAttribute("data-exam-icon") || "";
  const examSlug = element.getAttribute("data-exam-slug");
  let redirectUrl = (element.getAttribute("data-redirect-url") || "").trim();

  const selectDedupeKey = String(examName || "") + "|" + (window.location ? window.location.pathname : "");
  const selectNow = Date.now();
  if (!window.__PW_EXAM_SELECT_LAST__) {
    window.__PW_EXAM_SELECT_LAST__ = { key: "", ts: 0 };
  }
  if (
    window.__PW_EXAM_SELECT_LAST__.key === selectDedupeKey &&
    selectNow - window.__PW_EXAM_SELECT_LAST__.ts < 3000
  ) {
    return;
  }
  window.__PW_EXAM_SELECT_LAST__ = { key: selectDedupeKey, ts: selectNow };

  if (!examName) return;

  const textEl = options && options.textEl;
  const previousExamName = getPreviousExamName(textEl);

  if (typeof window.trackCohortChangeSubmit === "function") {
    try {
      window.trackCohortChangeSubmit({
        cohort_name: examName,
        exam_name: examName,
        exam_slug: examSlug,
        previous_cohort_name: previousExamName,
      });
    } catch (e) {}
  }

  try {
    persistExamSelection(examName, examIcon, redirectUrl);
  } catch (e) {}

  try {
    window.dispatchEvent(
      new CustomEvent("examSelected", {
        detail: {
          examName: examName,
          examIcon: examIcon,
          redirection_url: redirectUrl,
        },
      })
    );
  } catch (e) {}

  if (typeof options.onSelected === "function") options.onSelected(examName, examIcon);

  const navigationUrl = getExamNavigationUrl(redirectUrl);
  if (navigationUrl) {
    setTimeout(function () {
      window.location.href = navigationUrl;
    }, 150);
  }
}

class ExamSelectorComponent extends HTMLElement {
  connectedCallback() {
    if (!this._connected) {
      this._connected = true;
      this._onExamDisplayReady = this.#onExamDisplayReady.bind(this);
      this._onExamSelected = this.#onExamSelected.bind(this);
      this._onCardClick = this.#onCardClick.bind(this);
      this._onDragStart = this.#onDragStart.bind(this);
      this._onDragMove = this.#onDragMove.bind(this);
      this._onDragEnd = this.#onDragEnd.bind(this);
      window.addEventListener("examDisplayReady", this._onExamDisplayReady);
      window.addEventListener("examSelected", this._onExamSelected);
    }

    this.#restoreSavedExam();
    this.#observeTriggerRestore();
    this.#bindModal();
    this.#scheduleIdleHydrate();
  }

  disconnectedCallback() {
    window.removeEventListener("examDisplayReady", this._onExamDisplayReady);
    window.removeEventListener("examSelected", this._onExamSelected);
    this.#unbindDragDocument();
    if (this._pwRestoreObserver) {
      try {
        this._pwRestoreObserver.disconnect();
      } catch (e) {}
      this._pwRestoreObserver = null;
    }
    this._connected = false;
  }

  get #textEl() {
    return this.querySelector(".exam-selector__text");
  }

  get #iconEl() {
    return this.querySelector(".exam-selector__icon");
  }

  get #modalInner() {
    return this.querySelector(".exam-selector-modal__inner");
  }

  get #dialogComponent() {
    return this.querySelector("dialog-component.exam-selector-modal");
  }

  get #isTriggerOnly() {
    return this.getAttribute("data-trigger-only") === "true";
  }

  get #source() {
    return this.getAttribute("data-source") || "api";
  }

  get #defaultIconUrl() {
    return this.getAttribute("data-default-icon-url") || "";
  }

  #updateExamDisplay(name, icon) {
    paintExamSelectorTriggers(name, icon);
  }

  #restoreSavedExam() {
    restoreExamSelectorTriggers();
  }

  #observeTriggerRestore() {
    if (this._pwRestoreObserver) return;
    try {
      this.setAttribute("data-skip-subtree-update", "");
      this.setAttribute("data-skip-node-update", "");
    } catch (e) {}
    const root = this;
    const mo = new MutationObserver(function () {
      if (root._pwRestoreFromMo) return;
      const textEl = root.querySelector(".exam-selector__text");
      if (!textEl || !isPlaceholderExam(textEl.textContent)) return;
      root._pwRestoreFromMo = true;
      restoreExamSelectorTriggers();
      root._pwRestoreFromMo = false;
    });
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    this._pwRestoreObserver = mo;
  }

  #onExamDisplayReady(ev) {
    const fn = ev.detail && ev.detail.getSavedExamForDisplay;
    if (typeof fn === "function") {
      this._getSavedExamForDisplay = fn;
      if (typeof window.getSavedExamForDisplay !== "function") {
        window.getSavedExamForDisplay = fn;
      }
    }
    this.#restoreSavedExam();
  }

  #onExamSelected(ev) {
    const detail = (ev && ev.detail) || {};
    if (detail.examName || detail.examIcon) {
      this.#updateExamDisplay(detail.examName, detail.examIcon);
      return;
    }
    this.#restoreSavedExam();
  }

  #closeModal() {
    const dialogComponent = this.#dialogComponent;
    if (!dialogComponent) return;
    const dialog = dialogComponent.querySelector("dialog");
    if (dialog && dialog.open) {
      if (typeof dialogComponent.closeDialog === "function") dialogComponent.closeDialog();
      else if (dialog.close) dialog.close();
      else dialog.dispatchEvent(new Event("close", { bubbles: true }));
    }
  }

  #onCardClick(e) {
    const card = e.target.closest(".exam-selector-modal__card");
    if (!card) return;
    e.preventDefault();
    e.stopPropagation();
    const self = this;
    selectExamFromElement(card, {
      textEl: this.#textEl,
      onSelected: function (name, icon) {
        self.#updateExamDisplay(name, icon);
        setTimeout(function () {
          self.#closeModal();
        }, 100);
      },
    });
  }

  #bindModal() {
    if (this.#isTriggerOnly) return;
    const self = this;
    const modalInner = this.#modalInner;
    if (modalInner && modalInner.getAttribute("data-pw-exam-click-bound") !== "true") {
      modalInner.setAttribute("data-pw-exam-click-bound", "true");
      modalInner.addEventListener("click", this._onCardClick);
    }

    const trigger = this.querySelector(".exam-selector__trigger");
    if (trigger && trigger.getAttribute("data-pw-hydrate-bound") !== "true") {
      trigger.setAttribute("data-pw-hydrate-bound", "true");
      trigger.addEventListener("click", function () {
        self.#scheduleHydrate();
      });
    }

    const dialogComponent = this.#dialogComponent;
    if (!dialogComponent) return;
    const dialog = dialogComponent.querySelector("dialog");
    if (!dialog) return;

    if (!this._openBound) {
      this._openBound = true;
      dialog.addEventListener("open", function () {
        self.#scheduleHydrate();
        setTimeout(function () {
          self.#initializeDragHandle();
        }, 100);
      });
    }
    if (!this._openObserver) {
      this._openObserver = new MutationObserver(function () {
        if (dialog.hasAttribute("open")) {
          self.#scheduleHydrate();
          setTimeout(function () {
            self.#initializeDragHandle();
          }, 100);
        }
      });
      this._openObserver.observe(dialog, { attributes: true, attributeFilter: ["open"] });
    }
  }

  #initializeDragHandle() {
    const dialogComponent = this.#dialogComponent;
    if (!dialogComponent || window.innerWidth > 749) return;
    const dialog = dialogComponent.querySelector(".exam-selector-modal__content");
    const dragHandle = dialogComponent.querySelector(".exam-selector-modal__drag-handle");
    if (!dialog || !dragHandle) return;
    if (dragHandle.getAttribute("data-pw-drag-bound") === "true") return;
    dragHandle.setAttribute("data-pw-drag-bound", "true");
    this._dragDialog = dialog;
    dragHandle.addEventListener("touchstart", this._onDragStart, { passive: false });
    dragHandle.addEventListener("mousedown", this._onDragStart);
  }

  #onDragStart(e) {
    if (!this._dragDialog) return;
    this._dragging = true;
    this._dragStartY = e.touches ? e.touches[0].clientY : e.clientY;
    this._dragStartHeight = parseFloat(this._dragDialog.style.height) || 50;
    this._dragDialog.style.transition = "none";
    document.body.style.userSelect = "none";
    document.addEventListener("touchmove", this._onDragMove, { passive: false });
    document.addEventListener("touchend", this._onDragEnd);
    document.addEventListener("mousemove", this._onDragMove);
    document.addEventListener("mouseup", this._onDragEnd);
    e.preventDefault();
    e.stopPropagation();
  }

  #onDragMove(e) {
    if (!this._dragging || !this._dragDialog) return;
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = this._dragStartY - currentY;
    const newHeight = Math.max(50, Math.min(90, this._dragStartHeight + (deltaY / window.innerHeight) * 100));
    this._dragDialog.style.height = newHeight + "vh";
    e.preventDefault();
    e.stopPropagation();
  }

  #onDragEnd() {
    if (!this._dragging) return;
    this._dragging = false;
    this.#unbindDragDocument();
    if (!this._dragDialog) return;
    this._dragDialog.style.transition = "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
    document.body.style.userSelect = "";
    const currentHeight = parseFloat(this._dragDialog.style.height);
    if (currentHeight < 60) this._dragDialog.style.height = "50vh";
    else if (currentHeight > 80) this._dragDialog.style.height = "90vh";
  }

  #unbindDragDocument() {
    document.removeEventListener("touchmove", this._onDragMove);
    document.removeEventListener("touchend", this._onDragEnd);
    document.removeEventListener("mousemove", this._onDragMove);
    document.removeEventListener("mouseup", this._onDragEnd);
  }

  #modalIsEmpty() {
    const modalInner = this.#modalInner;
    if (!modalInner) return true;
    return modalInner.querySelector(".exam-selector-modal__card") == null;
  }

  #modalNeedsHydrate() {
    if (this.#isTriggerOnly) return false;
    if (this.#source !== "api") return false;
    return this.#modalIsEmpty();
  }

  #injectGroups(categoryGroups, modalInner) {
    const inner = modalInner || this.#modalInner;
    if (!inner) return false;
    const groups = categoryGroups || [];
    inner.innerHTML = buildExamSelectorModalInnerHTML({
      categoryGroups: groups,
      defaultIconUrl: this.#defaultIconUrl,
    });
    this.#bindModal();
    const sync = function () {
      syncSelectedCohortFromCurrentUrl(groups);
    };
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(sync);
    } else {
      setTimeout(sync, 0);
    }
    if (!this.#modalIsEmpty()) this.setAttribute(INIT_FLAG, "true");
    return !this.#modalIsEmpty();
  }

  #scheduleHydrate() {
    if (!this.#modalNeedsHydrate()) return;
    if (this._hydrateScheduled) return;
    this._hydrateScheduled = true;
    const self = this;
    const run = function () {
      self._hydrateScheduled = false;
      self.#hydrateIfNeeded();
    };
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(run);
    } else {
      setTimeout(run, 0);
    }
  }

  #scheduleIdleHydrate() {
    if (!this.#modalNeedsHydrate()) return;
    const self = this;
    const run = function () {
      self.#hydrateIfNeeded();
    };
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 0);
    }
  }

  #readQuery() {
    const fromAttr = this.getAttribute("data-exam-selector-query");
    if (fromAttr && fromAttr.trim()) return fromAttr.trim();
    const script = this.querySelector('script[type="application/graphql"], script[data-exam-selector-query]');
    if (script && script.textContent && script.textContent.trim()) return script.textContent.trim();
    return EXAM_SELECTOR_QUERY;
  }

  #hydrateIfNeeded() {
    if (this.#isTriggerOnly) return;
    if (this.#source !== "api") {
      this.setAttribute(INIT_FLAG, "true");
      const cached = getCachedExamData();
      if (cached && cached.categoryGroups && cached.categoryGroups.length > 0) {
        syncSelectedCohortFromCurrentUrl(cached.categoryGroups);
      } else {
        syncSelectedCohortFromModalCards(this);
      }
      return;
    }
    if (!this.#modalNeedsHydrate()) {
      const cached = getCachedExamData();
      if (cached && cached.categoryGroups && cached.categoryGroups.length > 0) {
        syncSelectedCohortFromCurrentUrl(cached.categoryGroups);
      } else {
        syncSelectedCohortFromModalCards(this);
      }
      return;
    }
    if (this._fetching) return;

    const token = (typeof window !== "undefined" && window.PW_STOREFRONT_TOKEN) || "";
    if (!token) {
      this._tokenRetries = (this._tokenRetries || 0) + 1;
      if (this._tokenRetries <= 40) {
        const self = this;
        setTimeout(function () {
          self.#hydrateIfNeeded();
        }, 50);
      }
      return;
    }

    const modalInner = this.#modalInner;
    if (!modalInner) {
      this._innerRetries = (this._innerRetries || 0) + 1;
      if (this._innerRetries <= 40) {
        const self = this;
        setTimeout(function () {
          self.#hydrateIfNeeded();
        }, 50);
      }
      return;
    }

    const cached = getCachedExamData();
    if (cached && cached.categoryGroups && cached.categoryGroups.length > 0) {
      this.#injectGroups(cached.categoryGroups, modalInner);
      if (!this.#modalIsEmpty()) return;
    }

    const query = this.#readQuery();
    if (!query) return;

    this._fetching = true;
    const self = this;
    fetchExamSelectorData(token, query).then(function (normalized) {
      self._fetching = false;
      const groups = (normalized && normalized.categoryGroups) || [];
      if (groups.length > 0) setCachedExamData(normalized);
      const inner = self.#modalInner || modalInner;
      self.#injectGroups(groups, inner);
    });
  }
}

function hydrateAllExamSelectors() {
  restoreExamSelectorTriggers();
  const roots = document.querySelectorAll("exam-selector-component");
  for (let i = 0; i < roots.length; i++) {
    const root = roots[i];
    if (root && typeof root.connectedCallback === "function") {
      try {
        root.connectedCallback();
      } catch (e) {}
    }
  }
}

function scheduleHydrateAll() {
  try {
    Promise.resolve().then(hydrateAllExamSelectors);
  } catch (e) {
    setTimeout(hydrateAllExamSelectors, 0);
  }
}

function scheduleRestoreAfterMorph() {
  scheduleHydrateAll();
  requestAnimationFrame(function () {
    restoreExamSelectorTriggers();
    setTimeout(restoreExamSelectorTriggers, 50);
    setTimeout(restoreExamSelectorTriggers, 250);
  });
}

if (!customElements.get("exam-selector-component")) {
  customElements.define("exam-selector-component", ExamSelectorComponent);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", scheduleHydrateAll);
} else {
  scheduleHydrateAll();
}

restoreExamSelectorTriggers();

document.addEventListener("shopify:section:load", scheduleRestoreAfterMorph);
document.addEventListener("cart:update", scheduleRestoreAfterMorph);
window.addEventListener("pageshow", restoreExamSelectorTriggers);

function examSelectorTriggerHandler() {
  let widgetName = "";
  try {
    const el = (typeof event !== "undefined" && event && event.currentTarget) || null;
    const root = el && el.closest && el.closest("exam-selector-component");
    widgetName = (root && root.getAttribute("data-widget-name")) || "";
  } catch (e) {}
  requestIdleCallback(() => {
    window.trackCohortChangeClick && window.trackCohortChangeClick({
      widget_name: widgetName
    })
  }, {timeout: 200})
}

window.ExamSelector = {
  selectFromElement: selectExamFromElement,
  hydrateAll: hydrateAllExamSelectors,
  restoreTriggers: restoreExamSelectorTriggers,
  paintTriggers: paintExamSelectorTriggers,
};
